import { Tag, type TagSize } from '@trustoo/components/Tag/v2'
import React, { useState } from 'react'
import { CodeSnippet, DemoAvatar, InlineCode, Section } from './shared'

// ---------- Tag (v2) showcase ----------
// Generated from Figma's "Tag" set (5:27307). The same monochrome chip the
// Input/Textarea `tags` variant renders.

const TAG_USAGE = `import { Tag } from '@trustoo/components/Tag/v2'

// Removable chip (this is what the Input/Textarea 'tags' variant renders)
<Tag size="sm" onClose={() => remove(id)}>Olivia</Tag>

// Selectable filter chip
<Tag checkbox checked={selected} onCheckedChange={setSelected}>Marketing</Tag>

// Leading adornments + count
<Tag dot>Active</Tag>
<Tag avatar={<Avatar />}>Olivia</Tag>
<Tag flag={<Flag />}>Netherlands</Tag>
<Tag count={5}>Label</Tag>`

const StatefulCheckboxTag: React.FC<{ children: React.ReactNode; initial?: boolean }> = ({
    children,
    initial = false,
}) => {
    const [checked, setChecked] = useState(initial)
    return (
        <Tag checkbox checked={checked} onCheckedChange={setChecked}>
            {children}
        </Tag>
    )
}

const INITIAL_TAGS = ['Olivia', 'Phoenix', 'Lana', 'Demi']

const RemovableTagsDemo: React.FC = () => {
    const [tags, setTags] = useState(INITIAL_TAGS)
    if (tags.length === 0) {
        return (
            <button
                type="button"
                onClick={() => setTags(INITIAL_TAGS)}
                className="text-xs font-medium text-text-brand-tertiary hover:underline">
                Reset tags
            </button>
        )
    }
    return (
        <div className="flex flex-wrap items-center gap-spacing-md">
            {tags.map((tag) => (
                <Tag
                    key={tag}
                    size="sm"
                    onClose={() => setTags((prev) => prev.filter((x) => x !== tag))}>
                    {tag}
                </Tag>
            ))}
        </div>
    )
}

export const TagShowcase: React.FC = () => (
    <Section
        title="Tag (v2 component)"
        description={
            <>
                Generated from Figma's <span className="font-semibold">Tag</span> set (ComponentSet{' '}
                <InlineCode>5:27307</InlineCode>). A monochrome removable / selectable chip — the
                same component the <span className="font-semibold">Input</span> and{' '}
                <span className="font-semibold">Textarea</span> <InlineCode>tags</InlineCode>{' '}
                variant renders. Import v2 explicitly via{' '}
                <InlineCode>@trustoo/components/Tag/v2</InlineCode>.
            </>
        }>
        <div className="space-y-spacing-3xl">
            <div className="space-y-spacing-md">
                <h3 className="text-sm font-semibold text-text-primary">Sizes</h3>
                <div className="flex flex-wrap items-center gap-spacing-lg rounded-radius-xs border border-border-secondary bg-base-white p-spacing-xl">
                    {(['sm', 'md', 'lg'] as TagSize[]).map((size) => (
                        <Tag key={size} size={size}>
                            {size.toUpperCase()}
                        </Tag>
                    ))}
                </div>
            </div>

            <div className="space-y-spacing-md">
                <h3 className="text-sm font-semibold text-text-primary">Leading adornments</h3>
                <div className="flex flex-wrap items-center gap-spacing-md rounded-radius-xs border border-border-secondary bg-base-white p-spacing-xl">
                    <Tag dot>Active</Tag>
                    <Tag avatar={<DemoAvatar />}>Olivia</Tag>
                    <Tag
                        flag={
                            <span className="flex h-full w-full items-center justify-center text-xs">
                                🇳🇱
                            </span>
                        }>
                        Netherlands
                    </Tag>
                </div>
            </div>

            <div className="space-y-spacing-md">
                <h3 className="text-sm font-semibold text-text-primary">Trailing actions</h3>
                <div className="flex flex-wrap items-center gap-spacing-md rounded-radius-xs border border-border-secondary bg-base-white p-spacing-xl">
                    <Tag onClose={() => undefined}>Removable</Tag>
                    <Tag count={5}>Count</Tag>
                    <Tag avatar={<DemoAvatar />} onClose={() => undefined}>
                        Olivia
                    </Tag>
                </div>
            </div>

            <div className="space-y-spacing-md">
                <div>
                    <h3 className="text-sm font-semibold text-text-primary">Selectable</h3>
                    <p className="mt-spacing-xs text-xs text-text-tertiary">
                        With <InlineCode>checkbox</InlineCode> the tag becomes a{' '}
                        <InlineCode>role=&quot;checkbox&quot;</InlineCode> button — click to toggle.
                    </p>
                </div>
                <div className="flex flex-wrap items-center gap-spacing-md rounded-radius-xs border border-border-secondary bg-base-white p-spacing-xl">
                    <StatefulCheckboxTag initial>Marketing</StatefulCheckboxTag>
                    <StatefulCheckboxTag>Design</StatefulCheckboxTag>
                    <StatefulCheckboxTag>Engineering</StatefulCheckboxTag>
                    <Tag checkbox disabled>
                        Disabled
                    </Tag>
                </div>
            </div>

            <div className="space-y-spacing-md">
                <div>
                    <h3 className="text-sm font-semibold text-text-primary">Removable list</h3>
                    <p className="mt-spacing-xs text-xs text-text-tertiary">
                        The pattern the input <InlineCode>tags</InlineCode> variant uses — each{' '}
                        <InlineCode>onClose</InlineCode> removes that chip.
                    </p>
                </div>
                <div className="rounded-radius-xs border border-border-secondary bg-base-white p-spacing-xl">
                    <RemovableTagsDemo />
                </div>
            </div>

            <div>
                <h3 className="mb-spacing-lg text-sm font-semibold text-text-primary">
                    How to use
                </h3>
                <CodeSnippet code={TAG_USAGE} />
                <p className="mt-spacing-lg text-xs text-text-tertiary">
                    Props: <InlineCode>size</InlineCode> (sm · md · lg),{' '}
                    <InlineCode>checkbox</InlineCode> + <InlineCode>checked</InlineCode> /{' '}
                    <InlineCode>onCheckedChange</InlineCode> (selectable),{' '}
                    <InlineCode>onClose</InlineCode> (removable), <InlineCode>count</InlineCode>,{' '}
                    <InlineCode>dot</InlineCode>, <InlineCode>flag</InlineCode>,{' '}
                    <InlineCode>avatar</InlineCode>, and <InlineCode>disabled</InlineCode>. Built-in
                    glyphs (check, close) use Material Symbols.
                </p>
            </div>
        </div>
    </Section>
)
